import { PopperProps } from '@mui/material'
import cn from 'classnames'
import DropdownMenu from 'ext-ui/components/Dropdown/components/DropdownMenu/DropdownMenu'
import DropdownOption, {
  DropdownOptionModel,
} from 'ext-ui/components/Dropdown/components/DropdownOption/DropdownOption'
import SingleDropdownHandle from 'ext-ui/components/Dropdown/components/SingleDropdownHandle/SingleDropdownHandle'
import {
  DropdownOptionGroup,
  DropdownValue,
  FreeTextOptions,
  useDropdownLogic,
} from 'ext-ui/components/Dropdown/hooks/useDropdownLogic'
import { TextInputProps } from 'ext-ui/components/TextInput/TextInput'
import React, {
  HTMLInputAutoCompleteAttribute,
  useImperativeHandle,
} from 'react'
import { useTranslate } from 'src/localization/Trns'
import { assertNever } from 'src/queries/agent/network/agent.network'
import { InfiniteScrollProps } from 'src/types'
import { BackendErrorPayload } from 'src/utils/error.utils'

import * as styles from './SingleDropdown.style'

export type { DropdownOptionGroup, DropdownValue }

const DropdownEmptyState: React.FC<{
  icon?: React.ReactNode
  title: string
  subtitle?: string
}> = ({ icon, title, subtitle }) => (
  <div className="DropdownEmptyState" css={styles.emptyState}>
    {icon}
    <div className={'title'}>{title}</div>
    {subtitle && <div className={'subtitle'}>{subtitle}</div>}
  </div>
)

export type SingleDropdownProps<T> = Omit<TextInputProps, 'iconRight'> & {
  selectedValue: DropdownValue<T> | undefined
  /**
   * Selection handler - supports both sync and async operations
   * Return Promise<void> for async operations with optimistic updates
   */
  onSelect: (selectedValue: DropdownValue<T>) => void | Promise<void>
  /**
   * Callback for when an async selection completes successfully
   * @remarks only called when onSelect returns a Promise
   * @param selectedValue the value that was selected
   */
  onSelectSuccess?: (selectedValue: DropdownValue<T> | undefined) => void
  /**
   * Callback for when an async selection fails
   * @remarks only called when onSelect returns a Promise
   * @param error
   */
  onSelectError?: (error: BackendErrorPayload<string>) => void
  /**
   * Callback for when an async selection completes, regardless of success or failure
   * @remarks only called when onSelect returns a Promise
   */
  onSelectSettled?: () => void
  onCancel?: () => void
  onReset?: () => void
  hideErrorMicrocopy?: boolean
  /**
   * whether to allow the user to enter arbitrary text into the dropdown as valid input
   */
  freeTextProps?: FreeTextOptions<T>
  infiniteScrollProps?: InfiniteScrollProps<string>
  openOnMount?: boolean
  /**
   * how many options are visible in the DropdownMenu before scrolling is necessary
   * @default 5
   */
  dropdownOptionsCount?: number
  /**
   * whether to show the search input inside the dropdown menu
   * @default false
   */
  searchable?: boolean
  emptyMessage?: {
    icon?: React.ReactNode
    title: string
    subtitle?: string
  }
  noResultsMessage?: {
    icon?: React.ReactNode
    title: string
    subtitle?: string
  }
  dropdownContainer?: PopperProps['container']
  /**
   * Hides the reserved leading checkmark column on each option in single-select
   * mode. Use for dropdowns whose options carry their own leading icon (e.g. a
   * card-network logo) and shouldn't be indented by the (otherwise empty)
   * selected-state column. No effect on multi-select dropdowns.
   * @default false
   */
  hideSelectedIcon?: boolean
  readOnly?: boolean
  /**
   * Whether the input shows a clear (×) affordance to unset the current value.
   * Defaults to true. Set false when the selection is mandatory (e.g. a role
   * that always resolves to something) so there's no misleading clear button.
   */
  clearable?: boolean
  /**
   * specifies whether the browser should enable autocomplete for the input field
   * NOTE: if using infiniteScrollProps, autocomplete will not work for items beyond the initial fetch limit
   */
  autoComplete?: HTMLInputAutoCompleteAttribute
  /**
   * Specifies what copy to use for the None option
   * NOTE: Set noneLabel='' if you don't want to show any none option
   */
  noneLabel?: string
  'data-testid': string
} & (
    | {
        options: DropdownOptionModel<T>[]
      }
    | {
        optionGroups: DropdownOptionGroup<T>[]
      }
  )

const SingleDropdownInner = <T,>(
  props: SingleDropdownProps<T>,
  externalRef: React.Ref<HTMLInputElement | null>,
) => {
  const {
    selectedValue,
    onSelect,
    onSelectSuccess,
    onSelectError,
    onSelectSettled,
    onCancel,
    onReset,
    hideErrorMicrocopy,
    freeTextProps,
    infiniteScrollProps,
    className,
    containerStyle,
    openOnMount,
    dropdownOptionsCount,
    searchable,
    emptyMessage = {
      title: 'No options available',
      subtitle: 'There are no options to select from at this time.',
    },
    noResultsMessage = {
      title: 'No matching results',
      subtitle: 'Please check for any typos, or search for a different option.',
    },
    dropdownContainer,
    hideSelectedIcon,
    variant = 'medium',
    state,
    label,
    errorText,
    placeholder,
    loadingState,
    disabled,
    readOnly,
    clearable = true,
    microcopy,
    requirement,
    style,
    autoComplete,
    tooltipPosition,
    tooltip,
    tooltipProps,
    noneLabel,
    iconLeft,
    'data-testid': testId,
  } = props

  const groups =
    'options' in props
      ? [
          {
            id: 'default',
            options: props.options,
          },
        ]
      : props.optionGroups

  const translate = useTranslate()

  const {
    rows,
    open,
    getMenuProps,
    getItemProps,
    getDropdownHandleProps,
    getSearchInputProps,
    containerRef,
    optimisticSelectedValues,
    asyncState,
    asyncError,
    handleSelect,
  } = useDropdownLogic({
    groups,
    selectedValues: selectedValue ? [selectedValue] : [],
    infiniteScrollProps,
    onSelect,
    onSelectSuccess,
    onSelectError,
    onSelectSettled,
    onCancel,
    openOnMount,
    freeTextProps,
    searchable,
    loadingState,
    noneLabel,
  })

  const findAutofillMatch = (autofillValue: string) => {
    const normalizedAutofill = autofillValue.toLowerCase().trim()

    // look through all groups and options for matches
    for (const group of groups) {
      for (const option of group.options) {
        const normalizedLabel = option.label?.toLowerCase()?.trim()
        const normalizedValue = option.value.toLowerCase().trim()

        if (
          normalizedLabel === normalizedAutofill ||
          normalizedValue === normalizedAutofill
        ) {
          return option
        }
      }
    }

    // if no exact match and freeText is enabled, use the autofilled value
    if (freeTextProps?.enabled) {
      return {
        value: autofillValue.trim(),
        label: autofillValue.trim(),
        details: freeTextProps?.freeTextOptionDetails,
      }
    }
  }

  const handleAutofillDetected = (autofillValue: string) => {
    const match = findAutofillMatch(autofillValue)
    if (match) {
      handleSelect(match)
    }
  }

  // Determine error text for microcopy display
  const displayErrorText =
    asyncError && !hideErrorMicrocopy ? asyncError.error : errorText

  const dropdownHandleProps = getDropdownHandleProps()

  // Forward the external ref to the internal handleRef for focus management
  useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(
    externalRef,
    () => dropdownHandleProps.ref.current?.input ?? null,
  )

  const getInputState = () => {
    if (state) return state
    if (displayErrorText) return 'error'
    if (open) return 'focused'
  }

  return (
    <div ref={containerRef} className={cn(className, 'w-full')}>
      <SingleDropdownHandle
        open={open}
        label={label}
        errorText={displayErrorText}
        variant={variant}
        state={getInputState()}
        placeholder={placeholder}
        disabled={disabled}
        loadingState={loadingState ?? asyncState}
        readOnly={readOnly}
        selectedValue={optimisticSelectedValues[0]}
        microcopy={microcopy}
        requirement={requirement}
        autoComplete={autoComplete}
        tooltipPosition={tooltipPosition}
        tooltip={tooltip}
        tooltipProps={tooltipProps}
        iconLeft={iconLeft}
        onAutofillDetected={handleAutofillDetected}
        searchable={searchable}
        containerStyle={containerStyle}
        style={style}
        onClear={
          clearable
            ? () => handleSelect({ value: '', label: translate('(none)') })
            : undefined
        }
        testId={testId}
        {...dropdownHandleProps}
      />
      <DropdownMenu
        {...getMenuProps()}
        variant={variant}
        container={dropdownContainer}
        open={open}
        dropdownOptionsCount={dropdownOptionsCount}
        searchProps={getSearchInputProps()}
        searchable={searchable}
        infiniteScrollProps={infiniteScrollProps}
        onReset={onReset}
        menuTestId={testId}
      >
        {rows.map((row, index) => {
          switch (row.kind) {
            case 'heading':
              return (
                <div
                  css={styles.groupHeading}
                  className={cn('GroupHeading', variant)}
                  key={`heading-${index}`}
                >
                  {row.heading}
                </div>
              )
            case 'loading':
              return (
                <div
                  css={styles.loadingOption}
                  className={cn('LoadingOption', variant)}
                  key={'loading'}
                  data-testid="dropdown-loading-state"
                >
                  {translate('Loading...')}
                </div>
              )
            case 'empty':
              return (
                <DropdownEmptyState
                  key={'empty'}
                  {...(row.hasSearchQuery ? noResultsMessage : emptyMessage)}
                />
              )
            case 'option': {
              const opt = row.option
              // never show (none) option as selected
              const selected =
                opt.value !== '' &&
                opt.value === optimisticSelectedValues[0]?.value
              return (
                <DropdownOption
                  key={`${opt.value}-${index}`}
                  {...opt}
                  selected={selected}
                  hideSelectedIcon={hideSelectedIcon}
                  variant={variant}
                  data-testid={
                    testId
                      ? `${testId}-option-${opt.value != null ? String(opt.value) : index}`
                      : `dropdown-option-${index}`
                  }
                  data-name={
                    typeof opt.label === 'string' ? opt.label : opt.value
                  }
                  {...getItemProps({ item: row, index })}
                />
              )
            }
            default:
              assertNever(row)
          }
        })}
      </DropdownMenu>
    </div>
  )
}

// Type assertion to maintain generic type parameter
const SingleDropdown = React.forwardRef(SingleDropdownInner) as <T = unknown>(
  props: SingleDropdownProps<T> & {
    ref?: React.Ref<HTMLInputElement | null>
  },
) => React.ReactElement

export default SingleDropdown
